import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Button } from '@mui/material';
import { Role, VisitorGroup } from 'graphql/generated/graphqlTypes';
import useAddOrEditVisitor from 'hooks/allVisitors/drawers/useAddOrEditVisitor';
import useTranslation from 'hooks/useTranslation/useTranslation';
import * as React from 'react';
import { useLayoutEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { isMaxNumberOfUsersReached } from 'utils/VisitorGroup/VisitorGroup';
import Loader from 'v2/components/Loader/Loader';
import useModalContext from 'v2/components/Modal/useModalContext';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import useWizardStepContext from 'v2/components/WizardModal/useWizardStepContext';
import { WIZARD_STEP_MIN_HEIGHT } from 'v2/constants';
import RolesSection, { RolesFormValues } from 'v2/pages/Users/Drawers/UserFormDrawer/RolesSection';
import { getUserPath } from 'v2/paths';
import { UserDetailsFormValues } from './UserDetailsStep';

const FORM_ID = 'add-user-wizard-roles-step-form';
const STEP_INDEX = 2;

interface Props {
  userDetails: UserDetailsFormValues | null;
  groupIds: string[];
  addOrEditVisitor: ReturnType<typeof useAddOrEditVisitor>['addOrEditVisitor'];
  administratorsVisitorGroups: VisitorGroup[] | undefined;
  installersVisitorGroups: VisitorGroup[] | undefined;
  rolesLoading: boolean;
}

const RolesStep: React.FC<Props> = ({
  userDetails,
  groupIds,
  addOrEditVisitor,
  administratorsVisitorGroups,
  installersVisitorGroups,
  rolesLoading
}) => {
  const { translation } = useTranslation();
  const { showSnackbar } = useSnackbarContext();
  const { closeModal } = useModalContext();
  const navigate = useNavigate();
  const { goToPrev, setFooterContent, steps } = useWizardStepContext();
  const [isLoading, setIsLoading] = React.useState(false);

  const isAdministratorsGroupFull = (administratorsVisitorGroups ?? []).some(isMaxNumberOfUsersReached);
  const isInstallersGroupFull = (installersVisitorGroups ?? []).some(isMaxNumberOfUsersReached);

  const { handleSubmit, control } = useForm<RolesFormValues>({
    defaultValues: { isAdministratorRole: false, isInstallerRole: false }
  });

  const onSubmit = handleSubmit(async (data) => {
    if (!userDetails) return;
    setIsLoading(true);
    try {
      const result = await addOrEditVisitor({
        isNewVisitor: true,
        name: userDetails.name,
        email: userDetails.email,
        visitorGroupMembershipInput: groupIds.map((visitorGroupId) => ({ visitorGroupId, role: Role.None })),
        shouldBeAdmin: data.isAdministratorRole,
        shouldBeInstaller: data.isInstallerRole
      });
      if (result?.data) {
        showSnackbar(translation.saved_changes, { severity: 'success' });
        closeModal();
        const newVisitorId = 'addVisitor' in result.data ? result.data.addVisitor?.id : undefined;
        if (newVisitorId) navigate(getUserPath(newVisitorId));
      }
    } finally {
      setIsLoading(false);
    }
  });

  useLayoutEffect(() => {
    setFooterContent(
      STEP_INDEX,
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', md: 'row' }, gap: 2 }}>
        <Button key="roles-step-back" id="roles-back-button" variant="outlined" type="button" onClick={goToPrev}>
          {translation.back}
        </Button>
        <Button
          key="roles-step-submit"
          id="roles-finish-button"
          variant="contained"
          type="submit"
          form={FORM_ID}
          loading={isLoading}
          endIcon={<ChevronRightIcon />}
        >
          {translation.finish}
        </Button>
      </Box>
    );
  }, [goToPrev, isLoading, setFooterContent, steps, translation]);

  return (
    <Box component="form" id={FORM_ID} onSubmit={onSubmit} noValidate sx={{ minHeight: WIZARD_STEP_MIN_HEIGHT }}>
      {rolesLoading ? (
        <Loader />
      ) : (
        <RolesSection
          control={control}
          disableAdministratorRole={isAdministratorsGroupFull}
          disableInstallerRole={isInstallersGroupFull}
          administratorDisabledTooltip={translation.group_is_full}
          installerDisabledTooltip={translation.group_is_full}
        />
      )}
    </Box>
  );
};

export default RolesStep;
